export enum AlertPermission {
  CREATE_BULLETIN = "alert.create_bulletin.",
  CREATE_GROUP = "alert.create_group.",
  DASHBOARD = "alert.dashboard.",
  VIEW_BULLETIN = "alert.view_bulletin.",
  SHARE_BULLETIN = "alert.share_bulletin.",
}

export enum ReportV2Permission {
  CREATE_SURVEY = "report_v2.create_survey.",
  VIEW_REPORTS = "report_v2.view_reports.",
  VIEW_INDIVIDUAL_REPORT = "report_v2.view_individual_report.",
  VIEW_CITIZEN_ENGAGE = "report_v2.view_citizen_engage.",
  VIEW_DASHBOARD = "report_v2.view_dashboard.",
  VIEW_SETTINGS = "report_v2.view_settings.",
  UPDATE_REPORTS = "report_v2.update_reports.",
  WRITE_AS_ORG = "report_v2.write_as_org.",
}

export enum RegistryPermission {
  VIEW_ENTRIES = "registry.view_entries.",
  UPDATE_ENTRIES = "registry.update_entries.",
  CREATE_ENTRIES = "registry.create_entries.",
  VIEW_DASHBOARD = "registry.view_dashboard.",
  VIEW_SETTINGS = "registry.view_settings.",
}

export enum IntelPermission {
  VIEW_ENTRIES = "intel.view_entries.",
  UPDATE_ENTRIES = "intel.update_entries.",
  DELETE_ENTRIES = "intel.delete_entries.",
  VIEW_DASHBOARD = "intel.view_dashboard.",
  VIEW_SETTINGS = "intel.view_settings.",
}

export enum FeedbackPermission {
  VIEW_FEEDBACK = "feedback.view_feedback.",
  VIEW_DASHBOARD = "feedback.view_dashboard.",
  VIEW_SETTINGS = "feedback.view_settings.",
}

export enum Permission {
  LOGIN = "login.",
  ALERT = "app.alert.",
  REPORT = "app.report.",
  REPORT_V2 = "app.report_v2.",
  CAMERA_REGISTRY = "app.camera_registry.",
  REGISTRY = "app.registry.",
  MESSAGE = "app.message.",
  CONNECT = "app.connect.",
  CONNECT_CREATE_MEETING = "connect.create_meeting.",
  CONNECT_VIEW_MEETINGS = "connect.view_meetings.",
  CONNECT_VIEW_RECORDINGS = "connect.view_recordings.",
  CONNECT_MANAGE_SETTINGS = "connect.manage_settings.",
  INTEL = "app.intel.",
  FEEDBACK = "app.feedback.",
  PARTNER = "app.partner.",
  RUBI_ORG_CREATE = "rubi_org.create.",
  RUBI_ORG_ANY_LIST_USERS = "rubi_org.any.list_users.",
  RUBI_ORG_OWN_LIST_USERS = "rubi_org.own.list_users.",
  RUBI_ORG_ANY_ADD_USER = "rubi_org.any.add_user.",
  RUBI_ORG_OWN_ADD_USER = "rubi_org.own.add_user.",
  RUBI_ORG_ANY_MODIFY_USER_APPS = "rubi_org.any.modify_user_apps.",
  RUBI_ORG_OWN_MODIFY_USER_APPS = "rubi_org.own.modify_user_apps.",
  RUBI_ORG_OWN_MANAGE_ADMINS = "rubi_org.own.manage_admins.",
  RUBI_ORG_ANY_MANAGE_ADMINS = "rubi_org.any.manage_admins.",
  RUBI_ORG_ANY_MANAGE_CONFIGURATION = "rubi_org.any.manage_configuration.",
  RUBI_USER_ANY_EDIT_FIELDS = "rubi_user.any.edit_fields.",
  RUBI_USER_ORG_EDIT_FIELDS = "rubi_user.org.edit_fields.",
  RUBI_USER_ANY_READ_PERMISSIONS = "rubi_user.any.read_permissions.",
  RUBI_USER_ORG_READ_PERMISSIONS = "rubi_user.org.read_permissions.",
  RUBI_USER_ANY_WRITE_PERMISSIONS = "rubi_user.any.write_permissions.",

  ALERT_CREATE_BULLETIN = AlertPermission.CREATE_BULLETIN,
  ALERT_DASHBOARD = AlertPermission.DASHBOARD,
  ALERT_CREATE_GROUP = AlertPermission.CREATE_GROUP,

  REPORT_V2_CREATE_SURVEY = ReportV2Permission.CREATE_SURVEY,
  REPORT_V2_VIEW_REPORTS = ReportV2Permission.VIEW_REPORTS,
  REPORT_V2_VIEW_INDIVIDUAL_REPORT = ReportV2Permission.VIEW_INDIVIDUAL_REPORT,
  REPORT_V2_VIEW_CITIZEN_ENGAGE = ReportV2Permission.VIEW_CITIZEN_ENGAGE,
  REPORT_V2_VIEW_DASHBOARD = ReportV2Permission.VIEW_DASHBOARD,
  REPORT_V2_VIEW_SETTINGS = ReportV2Permission.VIEW_SETTINGS,
  REPORT_V2_UPDATE_REPORTS = ReportV2Permission.UPDATE_REPORTS,
  REPORT_V2_WRITE_AS_ORG = ReportV2Permission.WRITE_AS_ORG,
  PARTNER_VIEW_REPORTS = "partner.view_reports.",
  PARTNER_ADD_SUPPLEMENTAL = "partner.add_supplemental.",
  PARTNER_VIEW_AS_ORG = "partner.view_as_org.",
  PARTNER_WRITE_AS_ORG = "partner.write_as_org.",

  REGISTRY_VIEW_ENTRIES = "registry.view_entries.",
  REGISTRY_UPDATE_ENTRIES = "registry.update_entries.",
  REGISTRY_CREATE_ENTRIES = "registry.create_entries.",
  REGISTRY_VIEW_DASHBOARD = "registry.view_dashboard.",
  REGISTRY_VIEW_SETTINGS = "registry.view_settings.",

  MESSAGE_CONVERSATION_READ = "conversation.org.read.",
  MESSAGE_CONVERSATION_WRITE = "conversation.org.write.",
  MESSAGE_TEMPLATE_READ = "conversation_template.org.read.",
  MESSAGE_TEMPLATE_WRITE = "conversation_template.org.write.",
  MESSAGE_CONVERSATION_ANY_READ = "conversation.any.read.",
  MESSAGE_TEMPLATE_ANY_WRITE = "conversation_template.any.write.",
  MESSAGE_FREEFORM_WRITE = "conversation.org.freeform_write.",
  MESSAGE_FREEFORM_ANY_WRITE = "conversation.any.freeform_write.",
  MESSAGE_VIEW_ALL_HISTORY = "conversation.org.view_all_history.",
  MESSAGE_VIEW_ALL_HISTORY_ANY = "conversation.any.view_all_history.",

  INTEL_VIEW_ENTRIES = IntelPermission.VIEW_ENTRIES,
  INTEL_UPDATE_ENTRIES = IntelPermission.UPDATE_ENTRIES,
  INTEL_DELETE_ENTRIES = IntelPermission.DELETE_ENTRIES,
  INTEL_VIEW_DASHBOARD = IntelPermission.VIEW_DASHBOARD,
  INTEL_VIEW_SETTINGS = IntelPermission.VIEW_SETTINGS,

  FEEDBACK_VIEW_FEEDBACK = FeedbackPermission.VIEW_FEEDBACK,
  FEEDBACK_VIEW_DASHBOARD = FeedbackPermission.VIEW_DASHBOARD,
  FEEDBACK_VIEW_SETTINGS = FeedbackPermission.VIEW_SETTINGS,
}

export interface CheckedUserPermissions {
  canManageApps: boolean;
  canManageAdmins: boolean;
  canModifyUsers: boolean;
  canCreateUsers: boolean;
  canAccessAlertsApp: boolean;
  canAccessReportsApp: boolean;
  canAccessReportsV2App: boolean;
  canAccessIntelApp: boolean;
  canAccessCameraRegistryApp: boolean;
  canAccessMessageApp: boolean;
  canAccessConnectApp: boolean;
  canCreateConnectMeeting: boolean;
  canViewConnectRecordings: boolean;
  canManageConnectSettings: boolean;
  canAccessFeedbackApp: boolean;
  canAccessPartnerApp: boolean;
  canLogin: boolean;
  canCreateBulletin: boolean;
  canAccessDashboard: boolean;
  canCreateGroup: boolean;
  canAccessRegistryApp: boolean;

  canCreateSurveyRV2: boolean;
  canViewReportsRV2: boolean;
  canViewIndividualReportRV2: boolean;
  canViewCitizenEngageRV2: boolean;
  canViewDashboardRV2: boolean;
  canViewSettingsRV2: boolean;
  canUpdateReportsRV2: boolean;

  canViewPartnerReports: boolean;
  canAddPartnerSupplemental: boolean;
  canViewPartnerAsOrg: boolean;
  canWritePartnerAsOrg: boolean;

  canViewDashboardRegistry: boolean;
  canViewEntriesRegistry: boolean;
  canUpdateEntriesRegistry: boolean;
  canCreateEntriesRegistry: boolean;
  canViewSettingsRegistry: boolean;

  canViewEntriesIntel: boolean;
  canUpdateEntriesIntel: boolean;
  canDeleteEntriesIntel: boolean;
  canViewDashboardIntel: boolean;
  canViewSettingsIntel: boolean;

  canWriteMessageTemplate: boolean;
  canSendFreeformMessage: boolean;
  canViewMessageAllHistory: boolean;

  canViewFeedback: boolean;
  canViewDashboardFeedback: boolean;
  canViewSettingsFeedback: boolean;
}

/** Label fields are i18n keys in the `common` namespace. */
export interface UserPermission {
  permission: Permission;
  labelKey: string;
  /** Abbreviated label for compact UIs such as DataGrid column headers. */
  shortLabelKey?: string;
  descriptionKey?: string;
  subPermissions?: UserPermission[];
}

/** An Access Overview card selection that narrows the users list. */
export type UsersListFilter =
  | { kind: "admins" }
  | { kind: "noAppAccess" }
  | { kind: "permission"; permission: Permission };
